import { env, financialAdvisorLogin, useAppSelector, useWebCollaborationLogic, webCollaborationMessages, webCollaborationSliceActions } from 'azshared';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AppButtonIcon } from '../../../components/button-icon/app-button-icon';
import { WebCollaborationPermissionRequestsDialog } from '../../../components/web-collaboration-permission-requests-dialog/web-collaboration-permission-requests-dialog';
import { useGlobalActions } from '../../../hooks/global-actions';
import { useInterval } from '../../../hooks/use-interval';
import './cf-room-page.scss';
import { AppDialog } from '../../../components/dialog/app-dialog';
import { AppButton, AppButtonType, AppButtonVariant } from '../../../components/buttons/app-button';

const mediaConstraints: MediaStreamConstraints = {
  video: true,
  audio: {
    echoCancellation: true,
    noiseSuppression: true
  }
};

export const CFRoomPage = () => {
  const { formatMessage } = useIntl();
  const globalActions = useGlobalActions();
  const channel = useRef<RTCDataChannel | null>(null);
  const { status, answer, acceptAnswer, createOffer, resetOffer } = useWebCollaborationLogic(globalActions);
  const cfVideo = useRef<HTMLVideoElement | null>(null);
  const prospectVideo = useRef<HTMLVideoElement | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const cfDraggableRef = useRef<HTMLDivElement>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const cfStreamRef = useRef<MediaStream | undefined>(undefined);
  const { roomID } = useParams();
  const [audioActive, setAudioActive] = useState<boolean>(true);
  const [cfVideoActive, setCfVideoActive] = useState<boolean>(true);
  const [prospectVideoActive, setProspectVideoActive] = useState<boolean>(true);
  const dispatch = useDispatch();
  const [countLogin, setCountLogin] = useState(0);
  const token = useAppSelector((state) => state.data?.user?.token);
  const [openReconnectingDialog, setOpenReconnectingDialog] = useState(false);
  const [isMifidCompleted, setIsMifidCompleted] = useState(false);
  const [isCallClosedByCf, setIsCallClosedByCf] = useState(false);
  const [isRoomExpired, setIsRoomExpired] = useState(false);
  const [isProspectDeviceMobile, setIsProspectDeviceMobile] = useState(true);
  const [isScreenSharingActive, setIsScreenSharingActive] = useState(false);
  const [videoSharingSize, setVideoSharingSize] = useState<{ width: number; height: number }>();
  const [prospectButtonsSize, setProspectButtonsSize] = useState<{ width: number; height: number }>();
  const [prospectButtonsTop, setProspectButtonsTop] = useState<number>(0);
  const [prospectBrowserLeft, setProspectBrowserLeft] = useState<number>(0);
  const [prospectScreenWidth, setProspectScreenWidth] = useState<number>(0);
  const [prospectBrowserWidth, setProspectBrowserWidth] = useState<number>(0);
  const [localAnswer, setLocalAnswer] = useState<string>();
  const [openPermissionRequestsDialog, setOpenPermissionRequestsDialog] = useState(false);
  const [isPlaying, setPlaying] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [connectionState, setConnectionState] = useState<RTCPeerConnectionState | undefined>(undefined);

  const openWait = useMemo(
    () =>
      (status !== 'CONNECTED' && connectionState !== 'connected') ||
      (!!connectionState && connectionState !== 'connected' && connectionState !== 'closed' && !isPlaying),
    [status, connectionState, isPlaying]
  );
  
  const negotiating = useRef(false);
  const lastIceCandiDateTime = useRef<number | undefined>(undefined);
  const offerSended = useRef(false);

  useEffect(() => {
    if (!token && countLogin === 0) {
      dispatch(financialAdvisorLogin());
      setCountLogin(countLogin + 1);
    }
  }, [token, dispatch, countLogin]);

  useEffect(() => {
    dispatch(webCollaborationSliceActions.setIsProspect(false));
    dispatch(webCollaborationSliceActions.setRoomId(roomID));
  }, [dispatch, roomID]);

  const stopStream = useCallback(() => {
    const tracks = cfStreamRef.current?.getTracks();
    tracks?.forEach((track) => {
      track.stop();
      track.enabled = false;
    });
    if (cfVideo.current) {
      cfVideo.current.srcObject = null;
    }
    if (prospectVideo.current) {
      prospectVideo.current.srcObject = null;
    }
    channel.current?.close();
    peerRef.current?.close();
    cfStreamRef.current = undefined;
    peerRef.current = null;
    negotiating.current = false;
    offerSended.current = false;
    lastIceCandiDateTime.current = undefined;
    setConnectionState('closed');
    setCfVideoActive(false);
    setIsScreenSharingActive(false);
  }, []);

  useEffect(() => {
    return () => {
      stopStream();
    };
  }, [stopStream]);

  const handleMessage = useCallback(
    (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.mifidStatus !== undefined) {
          setIsMifidCompleted(data.mifidStatus === 'completed');
          if (data.mifidStatus === 'completed') {
            stopStream();
          }
        }
        if (data.isMobile !== undefined) {
          setIsProspectDeviceMobile(data.isMobile);
        }
        if (data.screenSharingActive !== undefined) {
          setIsScreenSharingActive(data.screenSharingActive);
          setVideoSharingSize(data.windowSize);
          setProspectButtonsSize(data.buttonsSize);
          setProspectButtonsTop(data.buttonsTop);
          setProspectBrowserLeft(data.windowLeft);
          setProspectScreenWidth(data.screenWidth);
          setProspectBrowserWidth(data.browserWidth);
        }
        if (data.prospectVideoActive !== undefined) {
          setProspectVideoActive(data.prospectVideoActive);
        }
      } catch (e) {
        console.error('Error parsing message', e);
      }
    },
    [stopStream]
  );

  const handleNegotiationNeededEvent = useCallback(async (iceRestart: boolean) => {
    if (!negotiating.current && peerRef.current) {
      negotiating.current = true;
      try {
        const webcamStream = await navigator.mediaDevices.getUserMedia(mediaConstraints);
        if (cfVideo.current) {
          cfVideo.current.srcObject = webcamStream;
        }
        for (const track of webcamStream.getTracks()) {
          peerRef.current.addTrack(track, webcamStream);
        }
        setCfVideoActive(true);
        setAudioActive(true);
        cfStreamRef.current = webcamStream;
        offerSended.current = false;
        const offerStr = await peerRef.current.createOffer({ iceRestart });
        await peerRef.current.setLocalDescription(offerStr);
        setPlaying(true);
        lastIceCandiDateTime.current = Date.now();
      } catch (e) {
        console.error('Negotiation error', e);
        negotiating.current = false;
      }
    }
  }, []);

  const handleTrackEvent = useCallback((e: RTCTrackEvent) => {
    if (prospectVideo.current) {
      prospectVideo.current.srcObject = e.streams[0];
      remoteStreamRef.current = e.streams[0]; // Store the remote stream
    }
  }, []);

  const handleConnectionState = useCallback(() => {
    const state = peerRef.current?.connectionState;
    console.log('[CF] Peer connection state change:', state);
    if (state) setConnectionState(state);
    if (state === 'connected') {
      setOpenReconnectingDialog(false);
      negotiating.current = false;
    } else if (state === 'failed' || state === 'disconnected') {
      console.log('[CF] Connection lost, triggering reconnection');
      setOpenReconnectingDialog(true);
      setLocalAnswer(undefined);
      resetOffer();
      stopStream();
      setReconnecting(true);
    } else if (state === 'closed') {
      setOpenReconnectingDialog(true);
    }
  }, [stopStream, resetOffer]);

  const handleIceConnectionState = useCallback(() => {
    const state = peerRef.current?.iceConnectionState;
    console.log('[CF] ICE connection state change:', state);
    if (state === 'failed') {
      setLocalAnswer(undefined);
      stopStream();
      setReconnecting(true);
    }
  }, [stopStream]);

  useEffect(() => {
    if (isScreenSharingActive && prospectVideo.current && remoteStreamRef.current) {
      console.log('[CF] Forcing video element refresh for screen share.');
      prospectVideo.current.srcObject = null;
      prospectVideo.current.srcObject = remoteStreamRef.current;
    }
  }, [isScreenSharingActive]);

  useInterval(
    () => {
      const now = Date.now();
      if (!offerSended.current && lastIceCandiDateTime.current !== undefined && now - lastIceCandiDateTime.current > 1000) {
        if (peerRef.current?.localDescription) {
          console.log('[CF] Sending offer to signaling server');
          createOffer(JSON.stringify(peerRef.current.localDescription));
          setPlaying(false);
          lastIceCandiDateTime.current = undefined;
          offerSended.current = true;
        }
      }
    },
    isPlaying ? 500 : null
  );

  const handleIceCandidate = useCallback(
    (ev: RTCPeerConnectionIceEvent) => {
      if (ev.candidate === null && !offerSended.current) {
        if (peerRef.current?.localDescription) {
          console.log('[CF] ICE gathering complete, sending offer');
          createOffer(JSON.stringify(peerRef.current.localDescription));
          setPlaying(false);
          lastIceCandiDateTime.current = undefined;
          offerSended.current = true;
        }
      }
    },
    [createOffer]
  );

  const createPeer = useCallback(
    (locStatus: string) => {
      console.log('[CF] Creating new RTCPeerConnection, room status:', locStatus);
      const peer = new RTCPeerConnection({ iceServers: [{ urls: env.iceServers }] });
      peerRef.current = peer;
      peer.ontrack = handleTrackEvent;
      peer.onconnectionstatechange = handleConnectionState;
      peer.oniceconnectionstatechange = handleIceConnectionState;
      peer.onnegotiationneeded = () => handleNegotiationNeededEvent(locStatus === 'CONNECTED');
      peer.onicecandidate = handleIceCandidate;
      const dataChannel = peer.createDataChannel('data');
      channel.current = dataChannel;
      dataChannel.onmessage = handleMessage;
      return peer;
    },
    [handleTrackEvent, handleConnectionState, handleIceConnectionState, handleIceCandidate, handleNegotiationNeededEvent, handleMessage]
  );

  const callUser = useCallback(
    (locStatus: string) => {
      console.log('[CF] Initiating callUser');
      createPeer(locStatus);
    },
    [createPeer]
  );

  const handleCloseCall = useCallback(() => {
    if (channel.current?.readyState === 'open') {
      try {
        channel.current.send(JSON.stringify({ type: 'close' }));
      } catch (e) {
        console.error('Error sending close message', e);
      }
    }
    setIsCallClosedByCf(true);
    stopStream();
  }, [stopStream]);

  useEffect(() => {
    if (status) {
      console.log('[CF] Room status update:', status);
      if(status==='EXPIRED'){
        setIsRoomExpired(true);
        handleCloseCall();
      }
    }
    if (
      token &&
      (status === 'WAIT_OFFER' || status === 'CONNECTED' || status === 'WAIT_ANSWER' || status === 'WAIT_HANDSHAKE') &&
      (!peerRef.current || reconnecting)
    ) {
      if (reconnecting) setReconnecting(false);
      callUser(status);
    } else if (token && status === 'WAIT_OFFER' && peerRef.current) {
      console.log('[CF] Server requested WAIT_OFFER but peer exists. Restarting fresh to simulate page refresh.');
      stopStream();
      callUser(status);
    }
  }, [token, status, reconnecting, callUser, stopStream, handleCloseCall]);

  const handleAnswer = useCallback(
    (message: any) => {
      console.log('[CF] Received answer from signaling server');
      const desc = new RTCSessionDescription(message);
      peerRef.current?.setRemoteDescription(desc).catch((e: any) => console.log(e));
      acceptAnswer();
    },
    [acceptAnswer]
  );

  useEffect(() => {
    if (answer && status === 'WAIT_HANDSHAKE' && answer !== localAnswer) {
      handleAnswer(JSON.parse(answer));
      setLocalAnswer(answer);
    }
  }, [answer, handleAnswer, localAnswer, status]);

  const onToggleVideo = useCallback(async () => {
    if (cfStreamRef.current?.getVideoTracks()[0]) {
      if (cfStreamRef.current.getVideoTracks()[0].readyState === 'live') {
        cfStreamRef.current?.getVideoTracks()[0].stop();
        setCfVideoActive(false);
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        cfStreamRef.current.removeTrack(cfStreamRef.current.getVideoTracks()[0]);
        cfStreamRef.current.addTrack(stream.getVideoTracks()[0]);
        setCfVideoActive(true);
      }
    }
    peerRef.current
      ?.getSenders()
      ?.find((s: any) => s.track?.kind === 'video')
      ?.replaceTrack(cfStreamRef.current?.getVideoTracks()[0] || null);
  }, []);

  const onToggleAudio = useCallback(async () => {
    if (cfStreamRef.current?.getAudioTracks()[0]) {
      if (cfStreamRef.current.getAudioTracks()[0].readyState === 'live') {
        cfStreamRef.current?.getAudioTracks()[0].stop();
        setAudioActive(false);
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        cfStreamRef.current.removeTrack(cfStreamRef.current.getAudioTracks()[0]);
        cfStreamRef.current.addTrack(stream.getAudioTracks()[0]);
        if (cfVideo.current) cfVideo.current.srcObject = cfStreamRef.current;
        setAudioActive(true);
      }
      peerRef.current
        ?.getSenders()
        ?.find((s: any) => s.track?.kind === 'audio')
        ?.replaceTrack(cfStreamRef.current?.getAudioTracks()[0] || null);
    }
  }, []);



  useInterval(() => {
    if (channel.current?.readyState === 'open') {
      try {
        channel.current.send(JSON.stringify({ type: 'ping' }));
      } catch (e) {
        console.error('Error sending ping', e);
      }
    }
  }, 1000);

  useEffect(() => {
    if (channel.current?.readyState === 'open') {
      try {
        channel.current.send(JSON.stringify({ cfVideoActive }));
      } catch (e) {
        console.error('Error sending video status', e);
      }
    }
  }, [cfVideoActive]);

  const pageButtonsStyles = useMemo(() => {
    if (!isScreenSharingActive) return {};
    const videoProspectResizedHeight = (window.innerWidth * (videoSharingSize?.height ?? 0)) / (videoSharingSize?.width ?? 1);
    const videoProspectResizedWidth = (window.innerHeight * (videoSharingSize?.width ?? 0)) / (videoSharingSize?.height ?? 1);
    const videoTop = Math.max((window.innerHeight - videoProspectResizedHeight) / 2, 0);
    const minWidth = ((prospectButtonsSize?.width ?? 0) * videoProspectResizedWidth) / (videoSharingSize?.width ?? 1);
    const minHeight = ((prospectButtonsSize?.height ?? 0) * videoProspectResizedHeight) / (videoSharingSize?.height ?? 1);
    const top = videoTop + Math.max((prospectButtonsTop * window.innerHeight) / (videoSharingSize?.height ?? 1) - 15, 0);
    const left =
      prospectBrowserLeft !== null && prospectBrowserLeft !== undefined ? (prospectBrowserLeft - (prospectScreenWidth - prospectBrowserWidth)) / 2 : 0;
    return {
      top: `${top}px`,
      minWidth: `${minWidth}px`,
      minHeight: `${minHeight}px`,
      transform: `translateX(calc(-50% ${Math.sign(left) > 0 ? '+' : '-'} ${Math.abs(left)}px))`
    };
  }, [videoSharingSize, prospectButtonsSize, prospectButtonsTop, isScreenSharingActive, prospectBrowserLeft, prospectScreenWidth, prospectBrowserWidth]);

  const restoreCall = useCallback(async () => {
    setOpenPermissionRequestsDialog(false);
    if (peerRef.current?.connectionState !== 'connected') {
      negotiating.current = false;
      handleNegotiationNeededEvent(false);
    }
  }, [handleNegotiationNeededEvent]);

  useEffect(() => {
    const checkPerms = async () => {
      try {
        const perms = await Promise.all(['camera', 'microphone'].map((n) => navigator.permissions.query({ name: n as any })));
        perms.forEach(
          (p) =>
            (p.onchange = () => {
              if (p.state === 'denied') setOpenPermissionRequestsDialog(true);
            })
        );
      } catch (e) {
        console.error('Error checking permissions', e);
      }
    };
    checkPerms();
  }, []);

  const handleRecall = useCallback(() => {
    setCfVideoActive(true);
    setAudioActive(true);
    setIsScreenSharingActive(false);
    negotiating.current = false;
    callUser(status);
    setIsCallClosedByCf(false);
    handleNegotiationNeededEvent(status === 'CONNECTED');
  }, [callUser, status, handleNegotiationNeededEvent]);

  return (
    <div className='cf-room-page'>
      {!isMifidCompleted && (
        <div
          className={`cf-room-page-buttons ${isProspectDeviceMobile ? 'cf-room-page-buttons-mobile' : ''}`}
          style={
            !isProspectDeviceMobile ? { ...pageButtonsStyles, zIndex: openReconnectingDialog ? 10000 : 100 } : { zIndex: openReconnectingDialog ? 10000 : 100 }
          }>
          {!isCallClosedByCf && !isRoomExpired && (
            <>
              <AppButtonIcon
                icon='close-call'
                className='cf-room-page-icon-button'
                label={formatMessage(webCollaborationMessages.callButton)}
                onClick={handleCloseCall}
              />
              <AppButtonIcon
                icon={audioActive ? 'microphone-on' : 'microphone-off'}
                className='cf-room-page-icon-button'
                label={formatMessage(webCollaborationMessages.microphoneButton)}
                onClick={onToggleAudio}
              />
              <AppButtonIcon
                icon={cfVideoActive ? 'webcam-on' : 'webcam-off'}
                className='cf-room-page-icon-button'
                label={formatMessage(webCollaborationMessages.webcamButton)}
                onClick={onToggleVideo}
              />
            </>
          )}
        </div>
      )}
      {isCallClosedByCf && !isRoomExpired && (
        <div className='cf-room-page-recall-overlay'>
          <h2 className='cf-room-page-recall-text'>{formatMessage(webCollaborationMessages.callEnded)}</h2>
          <AppButton
            type={AppButtonType.BUTTON}
            variant={AppButtonVariant.Primary}
            label={formatMessage(webCollaborationMessages.reconnect)}
            onClick={handleRecall}
            className='cf-room-page-recall-button'
          />
        </div>
      )}
      {isRoomExpired && (
        <div className='cf-room-page-recall-overlay'>
          <h2 className='cf-room-page-recall-text'>{formatMessage(webCollaborationMessages.roomExpired)}</h2>
        </div>
      )}
      <div className={`prospect-avatar ${connectionState !== 'connected' || prospectVideoActive || isScreenSharingActive ? 'invisible' : ''}`} />
      <video
        id='prospectVideo'
        autoPlay
        ref={prospectVideo}
        className={`${connectionState !== 'connected' || (!prospectVideoActive && !isScreenSharingActive) ? 'invisible' : ''}`}
      />
      <div className='cf-room-page-videos' style={{ zIndex: openReconnectingDialog ? 10000 : 100 }}>
        <Draggable nodeRef={cfDraggableRef}>
          <div ref={cfDraggableRef} className={`cf-room-page-video ${isScreenSharingActive ? 'invisible' : ''}`}>
            <video autoPlay ref={cfVideo} className={!cfVideoActive ? 'invisible' : ''} muted />
          </div>
        </Draggable>
      </div>
      <AppDialog open={openWait || openReconnectingDialog || isMifidCompleted } className='web-collaboration-overlay-cf-dialog'>
        <div className='wait-for-cf-dialog'>
          <div className='wait-for-cf-dialog-section'>
            <p className='wait-for-cf-dialog-content'>
              {formatMessage(
                ((status && status !== 'WAIT_OFFER') || (!!connectionState && (connectionState === 'new' || connectionState === 'connecting'))) &&
                  !openReconnectingDialog
                  ? webCollaborationMessages.connecting
                  : webCollaborationMessages.waitForProspect
              )}
            </p>
          </div>
          <div className='wait-for-cf-dialog-button-section'>
            <AppButtonIcon
              icon='close-call'
              className='web-collaboration-overlay-icon-button'
              label={formatMessage(webCollaborationMessages.closeCallButton)}
              onClick={handleCloseCall}
            />
          </div>
        </div>
      </AppDialog>
      <WebCollaborationPermissionRequestsDialog open={openPermissionRequestsDialog} onClose={handleCloseCall} onContinue={restoreCall} />
    </div>
  );
};
